import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { loginPathForLastEntrance, useAuth } from '@/lib/auth'
import { AppShell } from '@/components/layout/AppShell'
import { LoginTabs } from '@/features/auth/LoginTabs'
import { SignupPage } from '@/features/auth/SignupPage'

import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { CategoriesPage } from '@/features/masters/CategoriesPage'
import { SessionTypesPage } from '@/features/masters/SessionTypesPage'
import { PricingRulesPage } from '@/features/masters/PricingRulesPage'
import { FeeHeadsPage } from '@/features/masters/FeeHeadsPage'
import { ChatPolicyPage } from '@/features/masters/ChatPolicyPage'
import { MentorsPage } from '@/features/mentors/MentorsPage'
import { StudentsPage } from '@/features/students/StudentsPage'
import { BookingsPage } from '@/features/bookings/BookingsPage'
import { PaymentsPage } from '@/features/payments/PaymentsPage'
import { FeedbackPage } from '@/features/feedback/FeedbackPage'
import { PlansPage } from '@/features/subscriptions/PlansPage'
import { CashRequestsPage } from '@/features/subscriptions/CashRequestsPage'
import { SettingsPage } from '@/features/settings/SettingsPage'
import { AdminAccountsPage } from '@/features/settings/AdminAccountsPage'
import { LibrariesPage } from '@/features/libraries/LibrariesPage'
import { SessionsPage } from '@/features/sessions/SessionsPage'
import { BookSessionPage } from '@/features/sessions/BookSessionPage'
import { MyPlanPage } from '@/features/plans/MyPlanPage'
import { MessagesPage } from '@/features/chat/MessagesPage'
import { ProfilePage } from '@/features/profile/ProfilePage'
import { AvailabilityPage } from '@/features/availability/AvailabilityPage'
import { LibraryRoomPage } from '@/features/libraries/LibraryRoomPage'
import { LeaderboardPage } from '@/features/libraries/LeaderboardPage'
import { AdminLibrariesPage } from '@/features/libraries/AdminLibrariesPage'

export function AppRoutes() {
  const { isAuthenticated, initialising, user } = useAuth()

  /*
    *** NOTHING RENDERS UNTIL THE SESSION IS KNOWN ***
    Reading localStorage is synchronous but happens in an effect, so there is
    one frame where `isAuthenticated` is false for an admin who IS signed in.
    Rendering during it flashes the login screen on every reload.
  */
  if (initialising) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          color: 'var(--text-muted)',
        }}
      >
        Loading…
      </div>
    )
  }

  /*
    Navigation only. A tampered role buys a menu that does not match the
    server's answer, never access to anything.
  */
  const isStaff =
    user?.role === 'TENANT_ADMIN' || user?.role === 'SUPER_ADMIN'
  /*
    *** A LEARNER LANDS ON THEIR SESSIONS, NOT THE LIBRARY ***
    One-to-one mentoring is the product; the library is sold alongside it
    and a tenant may not run one at all. Landing on an empty library screen
    would make the portal look like it had nothing in it.
  */
  const landing = isStaff ? '/dashboard' : '/sessions'

  return (
    <BrowserRouter>
      <Routes>
        {!isAuthenticated ? (
          <>
            {/*
              *** TWO WAYS IN, ONE PAGE ***
              `/login` is the platform entrance: mentor and admin tabs, no
              student tab, because a phone number alone cannot say which
              institute it belongs to.

              `/login/<slug>` is an institute's own entrance. The slug names
              the tenant — it is already public, printed in the merchant page
              URL the payment gateway requires — so the student tab works
              without an app key in the bundle. Pointing a client's domain
              here later puts their students in the right place by default.
            */}
            <Route path="/login" element={<LoginTabs />} />
            <Route path="/login/:slug" element={<LoginTabs />} />

            {/*
              Sign-up is institute-scoped for the same reason the student
              tab is: the slug names the tenant and the bundle carries no
              app key. `/signup` with no slug renders an explanation, not a
              form — there would be nothing to join.
            */}
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/signup/:slug" element={<SignupPage />} />
            {/*
              Everything else bounces to login. `replace` so the back button
              does not walk into a protected route the user cannot see.

              *** NOT ALWAYS `/login` ***
              That page is admin only. A student or mentor who signs out —
              or whose token expires — is sent back to their institute's own
              entrance, `/login/<slug>`, which the session carried and
              logout saved before clearing it. Only when we have no slug to
              work from does this fall back to the platform door.
            */}
            <Route
              path="*"
              element={<Navigate to={loginPathForLastEntrance()} replace />}
            />
          </>
        ) : (
          <>
            {/*
              *** SIGN-UP SURVIVES SIGNING IN, ON PURPOSE ***
              Step 2 of registration is the phone OTP, and that mints a
              session — the account is PENDING_VERIFICATION, which only
              SUSPENDED would refuse. The moment it does, `isAuthenticated`
              flips and this branch renders instead.

              Without this route the student would be dropped into the
              portal with an unverified email, which is precisely the gate
              step 3 exists to be. So the page stays mounted and keeps them
              on it until the emailed code is entered. Outside AppShell: it
              is not a portal screen and must not grow a sidebar.
            */}
            <Route path="/signup/:slug" element={<SignupPage />} />

            <Route element={<AppShell />}>
              {/*
                *** THE DASHBOARD IS AT /dashboard, NOT / ***
                The public marketing site owns the domain root, served by nginx
                from a different directory. While the dashboard lived at `/`,
                refreshing it left the single-page app entirely and loaded the
                marketing page — an admin pressing F5 on their own dashboard
                was thrown out of the portal. Reported 2026-09-18.

                Every other portal route is unaffected, because nginx only
                intercepts `/`, `/privacy`, `/terms`, `/site.css` and `/img/`.
              */}
              <Route path="/dashboard" element={<DashboardPage />} />

              {/* Anyone with an old bookmark to the SPA root is carried on
                  rather than bounced to the marketing site. This only fires
                  for in-app navigation; a hard refresh of `/` never reaches
                  React at all. */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              <Route path="/bookings" element={<BookingsPage />} />
              <Route path="/students" element={<StudentsPage />} />
              <Route path="/mentors" element={<MentorsPage />} />
              <Route path="/payments" element={<PaymentsPage />} />
              <Route
                path="/cash-requests"
                element={<CashRequestsPage />}
              />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/subscription-plans" element={<PlansPage />} />

              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/session-types" element={<SessionTypesPage />} />
              <Route path="/pricing" element={<PricingRulesPage />} />
              <Route path="/fee-heads" element={<FeeHeadsPage />} />
              <Route path="/chat-policy" element={<ChatPolicyPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/admins" element={<AdminAccountsPage />} />
              {/*
                *** THE LIBRARY IS FOR EVERY SIGNED-IN ROLE ***
                Students sit in it, mentors attend it, staff look in on it.
                The server decides what each of them may do — a student sees
                rooms they are entitled to, a mentor only those they are
                granted.

                Deliberately NOT behind a role guard. React routing is
                navigation, not protection: `auth_policy.py` is the security
                boundary and gives the same answer whatever the SPA renders.
              */}
              {/* Administration: create rooms, set capacity, grant
                  mentors. Server-gated to ADMIN and COORDINATOR. */}
              {/*
                One-to-one sessions: the caller's own bookings, whichever
                side they are on. `/bookings` is the admin's table of every
                session in the tenant and stays separate.
              */}
              <Route path="/sessions" element={<SessionsPage />} />
              {/* Students only in practice — a mentor reaching it sees no
                  mentors, because the list is scoped to the caller's own
                  student category. The server refuses the booking too. */}
              <Route path="/sessions/book" element={<BookSessionPage />} />

              {/*
                The STUDENT's own plan — balance, and what they may buy.
                `/subscription-plans` above is the admin screen that defines
                what the tenant sells; these are different questions asked by
                different people.

                Not role-guarded: React routing is navigation, not
                protection. `/me/subscription` answers for whoever asks and
                the page renders that answer.
              */}
              <Route path="/plan" element={<MyPlanPage />} />

              {/*
                The caller's own conversations, both roles. `/chat-threads`
                is the admin's support table and stays separate.
              */}
              <Route path="/messages" element={<MessagesPage />} />

              {/*
                Every role reaches this; the page itself decides what to
                show. An admin has no self-service profile and is told so
                rather than being sent to an endpoint that would 403.
              */}
              <Route path="/profile" element={<ProfilePage />} />

              {/*
                A mentor's own diary. Every endpoint behind it lives under
                `/mentor` and refuses anyone else; the page says so rather
                than firing four requests that 403.
              */}
              <Route path="/availability" element={<AvailabilityPage />} />

              <Route path="/libraries" element={<AdminLibrariesPage />} />
              <Route path="/library" element={<LibrariesPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              {/* A page inside the shell like every other route: same
                  sidebar, same header. The room draws its own columns
                  INSIDE the content area. */}
              <Route
                path="/library/:libraryId"
                element={<LibraryRoomPage />}
              />
            </Route>



            {/*
              *** WHERE EACH ROLE LANDS ***
              The dashboard is a staff screen. Sending a student or a mentor
              there shows them numbers they have no business seeing, on a
              page whose API calls will 403 — a broken-looking portal for
              someone who did nothing wrong.
            */}
            <Route path="/login" element={<Navigate to={landing} replace />} />
            {/* Signed in already — an institute login URL must not
                show the form again. */}
            <Route path="/login/:slug" element={<Navigate to={landing} replace />} />
            <Route path="*" element={<Navigate to={landing} replace />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  )
}
